import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { Verified } from "@mui/icons-material";
import * as Yup from "yup";

import messages from "../../../assets/locale/messages";
import { showHideLoader } from "../../../store/Loader/actions";
import Modal from "../../../components/Modal";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import { VouchersIcon } from "../../../utils/Icons/SidemenuIcons";
import { GreyProfileIcon } from "../../../utils/Icons";
import { PRICE_REGEX } from "../../../utils/Patterns";
import {
  voucherCompaniesLookupRequest,
  voucherCompaniesLookupResponse
} from "../../../store/Lookups/actions";
import { generateVoucherRequest } from "../../../store/Vouchers/actions";

const GenerateVoucherModal = ({ isOpen, onClose, companyData }) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);

  const companies = useSelector(
    (state) => state.lookups.voucherCompanies.list
  );

  const [companiesNamesListState, setCompaniesNamesListState] =
    useState([]);

  useEffect(() => {
    dispatch(voucherCompaniesLookupRequest());
    return () => {
      dispatch(voucherCompaniesLookupResponse([]));
    };
  }, []);

  useEffect(() => {
    if (companies)
      setCompaniesNamesListState([
        ...companies.map((company) => {
          return {
            id: company.Id,
            label: company.CompanyName,
            userId: company.UserId
          };
        })
      ]);
  }, [companies]);

  const {
    shared,
    vouchers: {
      generateVoucherModal: { title, labels, placeholders },
      generateButton
    }
  } = messages[lang];

  const initialValues = {
    company: null,
    voucherPercentage: ""
  };

  const handleCloseModal = () => {
    setValues({ ...initialValues });
    setTouched({});
    setErrors({});
    onClose();
  };

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    dirty,
    isValid,
    setValues,
    setTouched,
    setErrors
  } = useFormik({
    initialValues,
    validationSchema: Yup.object({
      company: Yup.object().required("companyNameRequired"),
      voucherPercentage: Yup.string()
        .matches(PRICE_REGEX, "invalidVoucherPercentage")
        .test(
          "percentage-limits",
          "voucherPercentageLimitExceeded",
          function (value) {
            if (value) {
              return (
                parseFloat(value) > 0 && parseFloat(value) <= 100
              );
            }
            return true;
          }
        )
        .required("voucherPercentageRequired"),
      voucherCode: Yup.string().nullable()
    }),
    onSubmit: ({ company, voucherPercentage }) => {
      dispatch(showHideLoader(true));
      const voucherData = {
        Id: companyData.isRequest ? companyData.companyId : 0,
        UserId: companyData.isRequest
          ? companyData.userId
          : company.userId,
        Code: companyData.voucherCode,
        IsRequest: companyData.isRequest,
        Percent: voucherPercentage
      };
      dispatch(
        generateVoucherRequest({
          data: voucherData,
          params: companyData.params,
          tabValue: companyData.tabValue
        })
      );
      dispatch(showHideLoader(false));
      handleCloseModal();
    }
  });
  useEffect(() => {
    if (companyData.companyName) {
      setValues({
        ...values,
        company: {
          id: companyData.companyId,
          label: companyData.companyName
        },
        voucherCode: companyData.voucherCode
      });
    }
  }, [companyData]);

  const renderLabelValue = (
    label,
    value,
    icon,
    valueEndAdornment
  ) => (
    <div className="d-flex align-items-center text-break">
      <div>{icon}</div>
      <div className="ms-3">
        <p className="text-light-grey mb-1 fsize-12 text-start">
          {label}
        </p>
        <p className="text-color fsize-15 fweight-500 mb-0 text-start">
          {value}
          <span>{valueEndAdornment}</span>
        </p>
      </div>
    </div>
  );

  const generateVoucherContent = () => (
    <div className="generate-voucher-form">
      <form noValidate onSubmit={handleSubmit}>
        {!!Object.keys(companyData).length &&
          companyData?.companyName && (
            <div className="my-4">
              {renderLabelValue(
                labels.companyName,
                companyData?.companyName || "--",
                <GreyProfileIcon dimensions={"25"} />,
                <Verified
                  color="primary"
                  className="ms-1"
                  sx={{ width: "12px" }}
                />
              )}
            </div>
          )}
        <div className="my-4">
          {renderLabelValue(
            labels.voucherCode,
            companyData?.voucherCode || "--",
            <VouchersIcon dimensions="25" />
          )}
        </div>
        {!companyData?.companyName && (
          <div className="my-4">
            <Select
              required
              key={labels.companyName}
              labelClassName="pb-1 font-medium"
              label={labels.companyName}
              placeholder={placeholders.companyName}
              options={companiesNamesListState}
              onChange={(value) => {
                setFieldTouched("company");
                setFieldValue("company", value);
              }}
              value={values.company}
              hasError={!!(touched.company && errors.company)}
              errMsg={errors.company}
              id="company"
              name="company"
            />
          </div>
        )}
        <Input
          endAdornment={"%"}
          required
          label={labels.voucherPercentage}
          placeholder={placeholders.voucherPercentage}
          onChange={(value) => {
            if (value?.trim() || !value) {
              const modifiedValue = value.replace(/\s\s+/g, " ");
              if (value[0] !== " ") {
                setFieldTouched("voucherPercentage");
                setFieldValue("voucherPercentage", modifiedValue);
              }
            }
          }}
          value={values.voucherPercentage}
          isInputHasErr={
            !!(touched.voucherPercentage && errors.voucherPercentage)
          }
          errMsg={errors.voucherPercentage}
          name="voucherPercentage"
          id="voucherPercentage"
          inputWrapperClass={"mb-4 mt-3 mt-md-0"}
        />
      </form>
    </div>
  );

  return (
    <Modal
      modalClass={"generate-voucher-modal"}
      open={isOpen}
      handleClose={handleCloseModal}
      title={title}
      showCloseIcon
      isContentText={false}
      content={generateVoucherContent()}
      confirmBtnTxt={generateButton}
      cancelBtnTxt={shared.cancel}
      handleConfirm={handleSubmit}
      handleCancel={handleCloseModal}
      disableConfirmBtn={!dirty || !isValid}
    />
  );
};

export default GenerateVoucherModal;

GenerateVoucherModal.propTypes = {
  isOpen: PropTypes.bool,
  companyData: PropTypes.object,
  onClose: PropTypes.func.isRequired
};
